import { QMNode } from './qm-node.js';

export const DEFAULT_CACHE_SIZE = 250;

export class TileCache {
  /** A map of all tiles currently in memory, keyed by their ID. */
  private readonly tiles: Map<string, QMNode> = new Map();

  /** The maximum number of tiles to keep in memory. */
  private readonly cacheSize: number = DEFAULT_CACHE_SIZE; // This can be tuned

  public add(tile: QMNode): void {
    this.tiles.set(tile.tileId, tile);
  }

  public get(id: string): QMNode | undefined {
    return this.tiles.get(id);
  }

  public getAll(): Array<QMNode> {
    return Array.from(this.tiles.values());
  }

  public clear(): void {
    this.tiles.clear();
  }

  /**
   * Prunes the cache based on a Least Recently Used (LRU) policy.
   * It finds tiles that were not touched in the current frame and unloads
   * the oldest ones until the cache is within its size limit.
   * @param {number} currentFrameNumber - The current frame number.
   */
  public prune(currentFrameNumber: number): void {
    if (this.tiles.size <= this.cacheSize) {
      return;
    }

    const unusedTiles: QMNode[] = [];
    for (const tile of this.tiles.values()) {
      // A tile is considered unused if it wasn't touched in the current frame.
      if (tile.lastTouchedFrame < currentFrameNumber) {
        unusedTiles.push(tile);
      }
    }

    // Sort unused tiles by which was seen least recently (oldest first).
    unusedTiles.sort((a, b) => a.lastTouchedFrame - b.lastTouchedFrame);

    // Unload the oldest tiles until we are back within the cache limit.
    const tilesToUnload = this.tiles.size - this.cacheSize;

    // let unloadedCount = 0;

    for (let i = 0; i < tilesToUnload && i < unusedTiles.length; i++) {
      const tileToUnload = unusedTiles[i];
      // if (tileToUnload.state === 'refined') {
      //   // We never unload refined tiles, as they are in active use.
      //   return;
      // }

      // The unload function will fully release resources and reset the tile.
      tileToUnload.unload();
      // unloadedCount++;

      this.tiles.delete(tileToUnload.tileId);
      // For a complete cleanup, we could also remove it from the cache map,
      // but keeping the node object itself is fine for reuse.
    }
  }
}
